import {
  mapFromAuditCriteriaGenerationStatusDTO,
  mapFromAuditCriteriaReviewSummaryDTO,
  mapFromAuditCriteriaSetDTO,
  mapFromAuditCriteriaStatusDTO,
  mapFromAICriterionOutcomeDTO,
  mapFromAuditProcedureRefDTO,
  mapFromEvidenceTaskAdviceDTO,
  mapFromEvidenceTaskBindingRowDTO,
  mapFromNormCriteriaStatsRowDTO,
  mapToAuditCriteriaEditDTO,
  mapToAuditCriterionStateDTO,
  mapToAuditCriteriaGenerationStartDTO,
  mapToAuditCriteriaStatusQueryDTO,
  mapToCreateEvidenceTaskRequestDTO,
  mapToEvidenceTaskBindEditDTO,
} from 'mappings/auditCriteriaMapping';
import AuditCriteriaSet, {
  AuditCriteriaGenerationStatus,
  AuditCriteriaReviewSummary,
  AuditCriteriaStatus,
  AuditCriterion,
  auditCriterionStateActive,
  auditCriterionStateArchived,
  AuditProcedureRef,
  EvidenceTaskAdvice,
  EvidenceTaskBindingRow,
  NormCriteriaStatsRow,
} from 'models/auditCriteria';
import AuditCriteriaSetDTO, {
  AuditCriteriaGenerationStatusDTO,
  AuditCriteriaReviewSummaryDTO,
  AuditCriteriaStatusDTO,
  AuditProcedureRefDTO,
  EvidenceTaskAdviceDTO,
  EvidenceTaskBindingRowDTO,
  NormCriteriaStatsRowDTO,
} from 'models/dto/auditCriteriaDTO';
import {
  mapFromAuditCriteriaValuesResultDTO,
  mapToAuditCriteriaValuesQueryDTO,
} from 'mappings/auditCriteriaValuesMapping';
import { AuditCriteriaPeriod, AuditCriteriaValues } from 'models/auditCriteriaValues';
import {
  mapFromTemplateCriteriaImportPreviewDTO,
  mapFromTemplateCriteriaImportResultDTO,
  mapToTemplateCriteriaImportRequestDTO,
} from 'mappings/templateCriteriaImportMapping';
import {
  TemplateCriteriaImportPreview,
  TemplateCriteriaImportRequest,
  TemplateCriteriaImportResult,
} from 'models/templateCriteriaImport';
import {
  TemplateCriteriaImportPreviewDTO,
  TemplateCriteriaImportResultDTO,
} from 'models/dto/templateCriteriaImportDTO';
import AppError from 'utils/appError';
import http from './httpService';
// PLAN-075 Task 14
import {
  mapFromAssessmentAutomationsDTO,
  mapFromAssessmentTaskCreatedDTO,
  mapFromAssessmentTaskResultDTO,
  mapFromAuditCriteriaCompareDTO,
  mapFromGenericAuditCriteriaSetRowDTO,
  mapToAssessmentTaskCreateDTO,
  mapToGenericAuditCriteriaSetCreateDTO,
} from 'mappings/auditCriteriaGenericMapping';
import {
  AICriterionOutcome,
  AssessmentAutomations,
  AssessmentTaskCreate,
  AssessmentTaskCreated,
  AssessmentTaskResult,
  AuditCriteriaCompare,
  GenericAuditCriteriaSetRow,
} from 'models/auditCriteriaGenericSets';
import {
  AICriterionOutcomeDTO,
  AssessmentAutomationsDTO,
  AssessmentTaskCreatedDTO,
  AssessmentTaskResultDTO,
  AuditCriteriaCompareDTO,
  AuditCriteriaValuesResultDTO,
  GenericAuditCriteriaSetRowDTO,
} from 'models/dto/auditCriteriaGenericDTO';

// queueReevaluation false reads the set without starting a re-evaluation of a flagged set (a read-only view)
export async function apiGetAuditCriteria(
  entityType: number,
  entityId: number,
  accessToken: string,
  queueReevaluation: boolean = true,
): Promise<AuditCriteriaSet> {
  try {
    const query = queueReevaluation ? '' : '?queueReevaluation=false';
    const res = await http.get<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}${query}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSaveAuditCriteria(
  entityType: number,
  entityId: number,
  criteria: AuditCriterion[],
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.put<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}`,
      mapToAuditCriteriaEditDTO(criteria),
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Moves the given criteria to a state (auditCriterionState*): Active, NotApplicable or Archived. Only the
 * transitions a person may make are accepted (Active to and from NotApplicable, Active to and from
 * Archived); the server refuses the rest with a reason. Deliberately not part of the whole-set PUT - the
 * state and its stamps are server-owned, so the client sends the intent only. Returns the refreshed set.
 */
export async function apiSetAuditCriteriaState(
  entityType: number,
  entityId: number,
  criterionKeys: string[],
  state: string,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/state`,
      mapToAuditCriterionStateDTO(criterionKeys, state),
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** Archives the given criteria: they leave the active set but keep their history and their evidence task bindings. */
export async function apiArchiveAuditCriteria(
  entityType: number,
  entityId: number,
  criterionKeys: string[],
  accessToken: string,
): Promise<AuditCriteriaSet> {
  return apiSetAuditCriteriaState(entityType, entityId, criterionKeys, auditCriterionStateArchived, accessToken);
}

/** Puts archived criteria back in the active set: they are audited and projected onto the procedure forms again. */
export async function apiUnarchiveAuditCriteria(
  entityType: number,
  entityId: number,
  criterionKeys: string[],
  accessToken: string,
): Promise<AuditCriteriaSet> {
  return apiSetAuditCriteriaState(entityType, entityId, criterionKeys, auditCriterionStateActive, accessToken);
}

/**
 * Records the human review of an entity's criteria, or withdraws it. Separate from the save on
 * purpose: a save says what the criteria ARE, this says a human has read them since they last
 * changed. The stamp itself is server-owned - the client only says which of the two it means.
 */
export async function apiSetAuditCriteriaReviewed(
  entityType: number,
  entityId: number,
  reviewed: boolean,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/review`,
      { reviewed: reviewed },
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGenerateAuditCriteria(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/generate`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Lets the AI re-evaluate the set against its current sources: unaffected criteria stay as they are,
 * changed obligations are reworded, new ones added, AI-derived ones no source restates archived.
 * Returns the set as GET does. 403 when AI is off; the auto-sync setting gates the automatic paths only.
 */
export async function apiReevaluateAuditCriteria(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/reevaluate`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Asks the AI for advice on the evidence tasks bound to the entity's one-time criteria and returns
 * the refreshed set. Advice only: a completed or archived task verifies its criterion whatever the
 * AI says. Controls and requirements only.
 */
export async function apiAdviseAuditCriteriaEvidenceTasks(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/evidencetasks/advice`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The stored AI advice on one evidence task for this entity, with the report rendered as markdown.
 */
export async function apiGetEvidenceTaskAdvice(
  entityType: number,
  entityId: number,
  taskId: number,
  accessToken: string,
): Promise<EvidenceTaskAdvice> {
  try {
    const res = await http.get<EvidenceTaskAdviceDTO>(
      `/criteria/${entityType}/${entityId}/evidencetasks/${taskId}/advice`,
      http.getRequestConfig(accessToken),
    );

    return mapFromEvidenceTaskAdviceDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetEvidenceTaskBindings(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<EvidenceTaskBindingRow[]> {
  try {
    const res = await http.get<EvidenceTaskBindingRowDTO[]>(
      `/criteria/${entityType}/${entityId}/evidencetasks`,
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromEvidenceTaskBindingRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiBindEvidenceTask(
  entityType: number,
  entityId: number,
  taskId: number,
  criterionKeys: string[],
  accessToken: string,
): Promise<EvidenceTaskBindingRow[]> {
  try {
    const res = await http.put<EvidenceTaskBindingRowDTO[]>(
      `/criteria/${entityType}/${entityId}/evidencetasks/${taskId}`,
      mapToEvidenceTaskBindEditDTO(criterionKeys),
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromEvidenceTaskBindingRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Removes the criterion's evidence task. A task the criterion path created and no other criterion
 * holds is deleted when deleteTask says so, and otherwise becomes an ordinary task of the item;
 * a task somebody selected is only unbound.
 */
export async function apiUnbindEvidenceTask(
  entityType: number,
  entityId: number,
  taskId: number,
  criterionKey: string,
  deleteTask: boolean,
  accessToken: string,
): Promise<EvidenceTaskBindingRow[]> {
  try {
    const res = await http.delete<EvidenceTaskBindingRowDTO[]>(
      `/criteria/${entityType}/${entityId}/evidencetasks/${taskId}?criterionKey=${encodeURIComponent(
        criterionKey,
      )}&deleteTask=${deleteTask ? 'true' : 'false'}`,
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromEvidenceTaskBindingRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Creates an evidence task for the given criteria on the server: a Hidden task, bound to the criteria,
 * assigned to the entity's owner. Returns the entity's refreshed bindings; the new task is the one
 * bound to every requested key that was not bound before.
 */
export async function apiCreateEvidenceTask(
  entityType: number,
  entityId: number,
  criterionKeys: string[],
  title: string,
  accessToken: string,
): Promise<EvidenceTaskBindingRow[]> {
  try {
    const res = await http.post<EvidenceTaskBindingRowDTO[]>(
      `/criteria/${entityType}/${entityId}/evidencetasks`,
      mapToCreateEvidenceTaskRequestDTO(criterionKeys, title),
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromEvidenceTaskBindingRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Deletes an evidence task the criterion path created and nobody filled (Hidden, open, empty, bound to
 * this entity only). The server refuses anything else with a 400 that names the reason.
 */
export async function apiDiscardEvidenceTask(
  entityType: number,
  entityId: number,
  taskId: number,
  accessToken: string,
): Promise<EvidenceTaskBindingRow[]> {
  try {
    const res = await http.delete<EvidenceTaskBindingRowDTO[]>(
      `/criteria/${entityType}/${entityId}/evidencetasks/${taskId}/discard`,
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromEvidenceTaskBindingRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Starts the criteria generation run of a standard for the given items (empty = every control and
 * requirement of the standard). Items that already have criteria are left untouched; the run prepares
 * up to 25 items per start and the tab starts again while items remain.
 */
export async function apiStartCriteriaGeneration(
  normId: number,
  items: { entityType: number; entityId: number }[] | undefined,
  accessToken: string,
): Promise<AuditCriteriaGenerationStatus> {
  try {
    const res = await http.post<AuditCriteriaGenerationStatusDTO>(
      `/criteria/standard/${normId}/generation/start`,
      mapToAuditCriteriaGenerationStartDTO(items),
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaGenerationStatusDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiStopCriteriaGeneration(normId: number, accessToken: string): Promise<AuditCriteriaGenerationStatus> {
  try {
    const res = await http.post<AuditCriteriaGenerationStatusDTO>(
      `/criteria/standard/${normId}/generation/stop`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaGenerationStatusDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetCriteriaGenerationStatus(
  normId: number,
  accessToken: string,
  abortSignal?: AbortSignal,
): Promise<AuditCriteriaGenerationStatus> {
  try {
    const res = await http.get<AuditCriteriaGenerationStatusDTO>(
      `/criteria/standard/${normId}/generation`,
      http.getRequestConfig(accessToken, undefined, undefined, abortSignal),
    );

    return mapFromAuditCriteriaGenerationStatusDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The per-control criteria stats of a tenant standard (norm id), ISO or custom: the Frameworks
 * criteria pivot's grid.
 */
export async function apiGetStandardCriteriaStats(
  normId: number,
  accessToken: string,
  abortSignal?: AbortSignal,
): Promise<NormCriteriaStatsRow[]> {
  try {
    const res = await http.get<NormCriteriaStatsRowDTO[]>(
      `/criteria/standard/${normId}/stats`,
      http.getRequestConfig(accessToken, undefined, undefined, abortSignal),
    );

    return (res.data ?? []).map(mapFromNormCriteriaStatsRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The criteria sets flagged for re-evaluation: the count and the oldest few named, for the
 * dashboard widget. A manager's read; nothing here calls a model.
 */
export async function apiGetAuditCriteriaReviewSummary(
  top: number,
  accessToken: string,
): Promise<AuditCriteriaReviewSummary> {
  try {
    const res = await http.get<AuditCriteriaReviewSummaryDTO>(
      `/criteria/review/summary?top=${top}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaReviewSummaryDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Batch "do these entities have criteria" question - the AI-audit start dialog's counts.
 */
export async function apiQueryAuditCriteriaStatus(
  items: { entityType: number; entityId: number }[],
  accessToken: string,
): Promise<AuditCriteriaStatus[]> {
  try {
    const res = await http.post<AuditCriteriaStatusDTO[]>(
      `/criteria/status/query`,
      mapToAuditCriteriaStatusQueryDTO(items),
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromAuditCriteriaStatusDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The library procedures covering an entity: the frameworks overview's route into the procedure
 * form editor.
 */
export async function apiGetProceduresForEntity(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditProcedureRef[]> {
  try {
    const res = await http.get<AuditProcedureRefDTO[]>(
      `/criteria/${entityType}/${entityId}/procedures`,
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromAuditProcedureRefDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// PLAN-075 Task 14: generic criteria sets, the copy and revert of an item's set, assessment tasks, "Answered by" and the
// AI outcomes. A generic set's criteria are read and written through the item routes above under the set's row key
// (the family, and 0 or -(subType + 1)); only the list, the create and the delete have generic routes of their own.
//

/** The family's generic sets: the family set, then one per subtype that has a set. */
export async function apiGetGenericAuditCriteriaSets(
  entityType: number,
  accessToken: string,
): Promise<GenericAuditCriteriaSetRow[]> {
  try {
    const res = await http.get<GenericAuditCriteriaSetRowDTO[]>(
      `/criteria/generic/family/${entityType}`,
      http.getRequestConfig(accessToken),
    );

    return (res.data ?? []).map(mapFromGenericAuditCriteriaSetRowDTO);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** A new generic set of a family: empty, or a subtype set started as a copy of the family set. */
export async function apiCreateGenericAuditCriteriaSet(
  entityType: number,
  subType: number | undefined,
  copyFromFamilySet: boolean,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      '/criteria/generic',
      mapToGenericAuditCriteriaSetCreateDTO(entityType, subType, copyFromFamilySet),
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** What adding a template's checklist and form to a generic set would add, and what not with why (PLAN-CTI-081). */
export async function apiPreviewTemplateCriteriaImport(
  auditCriteriaSetId: number,
  request: TemplateCriteriaImportRequest,
  accessToken: string,
): Promise<TemplateCriteriaImportPreview> {
  try {
    const res = await http.post<TemplateCriteriaImportPreviewDTO>(
      `/criteria/generic/${auditCriteriaSetId}/templateimport/preview`,
      mapToTemplateCriteriaImportRequestDTO(request),
      http.getRequestConfig(accessToken),
    );

    return mapFromTemplateCriteriaImportPreviewDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** Adds the new criteria of a template's checklist and form to a generic set; duplicates by name are skipped. */
export async function apiImportTemplateCriteria(
  auditCriteriaSetId: number,
  request: TemplateCriteriaImportRequest,
  accessToken: string,
): Promise<TemplateCriteriaImportResult> {
  try {
    const res = await http.post<TemplateCriteriaImportResultDTO>(
      `/criteria/generic/${auditCriteriaSetId}/templateimport`,
      mapToTemplateCriteriaImportRequestDTO(request),
      http.getRequestConfig(accessToken),
    );

    return mapFromTemplateCriteriaImportResultDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** Deletes a generic set; refused (AUDIT0037) while a copy or a subtype set names it as its source, unless detached. */
export async function apiDeleteGenericAuditCriteriaSet(
  auditCriteriaSetId: number,
  detach: boolean,
  accessToken: string,
): Promise<void> {
  try {
    await http.delete(
      `/criteria/generic/${auditCriteriaSetId}?detach=${detach ? 'true' : 'false'}`,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** "Change for this item": the item gets its own copy of the generic set it uses. Returns the item's set. */
export async function apiCopyAuditCriteriaForEntity(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/copy`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** "Use the generic set from now on": deletes the item's own set; refused (AUDIT0032) while an open evidence task is bound. */
export async function apiRevertAuditCriteriaToGeneric(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditCriteriaSet> {
  try {
    const res = await http.post<AuditCriteriaSetDTO>(
      `/criteria/${entityType}/${entityId}/revert`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaSetDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** The item's copy beside the generic set it was copied from, criterion by criterion. */
export async function apiCompareAuditCriteriaWithSource(
  entityType: number,
  entityId: number,
  accessToken: string,
): Promise<AuditCriteriaCompare> {
  try {
    const res = await http.get<AuditCriteriaCompareDTO>(
      `/criteria/${entityType}/${entityId}/compare`,
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaCompareDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** An assessment task for one item on its active set; refused (AUDIT0039) when no set resolves. */
export async function apiCreateAssessmentTask(
  entityType: number,
  entityId: number,
  create: AssessmentTaskCreate,
  accessToken: string,
): Promise<AssessmentTaskResult> {
  try {
    const res = await http.post<AssessmentTaskResultDTO>(
      `/criteria/${entityType}/${entityId}/assess`,
      mapToAssessmentTaskCreateDTO(create),
      http.getRequestConfig(accessToken),
    );

    return mapFromAssessmentTaskResultDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** Dismisses an AI outcome from the criteria view; the outcome stays as history. */
export async function apiDismissCriterionOutcome(outcomeId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/criteria/outcomes/${outcomeId}/dismiss`, {}, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

// PLAN-075 Task 15: the criteria values of a list
//

/**
 * The criteria columns of a family's list and the latest answer per entity and column in the period. The server
 * reduces the ids to the entities the user may read; an id it leaves out is absent from the values.
 */
export async function apiQueryAuditCriteriaValues(
  entityType: number,
  entityIds: number[] | undefined,
  period: AuditCriteriaPeriod,
  accessToken: string,
  lineageKeys?: string[],
): Promise<AuditCriteriaValues> {
  try {
    const res = await http.post<AuditCriteriaValuesResultDTO>(
      `/criteria/values/query`,
      mapToAuditCriteriaValuesQueryDTO(entityType, entityIds, period, lineageKeys),
      http.getRequestConfig(accessToken),
    );

    return mapFromAuditCriteriaValuesResultDTO(res.data ?? new AuditCriteriaValuesResultDTO());
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//
// PLAN-075 Task 16: the assessment start card, the batch create, the starts of the chosen automations and one outcome.
//

/**
 * The start card of an assessment (spec 5.8): the automations the items' sets bind and the suggestions for their
 * unbound criteria. One item asks its own route; several ask the family's.
 */
export async function apiGetAssessmentAutomations(
  entityType: number,
  entityIds: number[],
  accessToken: string,
): Promise<AssessmentAutomations> {
  try {
    const url =
      entityIds.length === 1
        ? `/criteria/${entityType}/${entityIds[0]}/assess/automations`
        : `/criteria/${entityType}/assess/automations?${entityIds.map((id) => `entityIds=${id}`).join('&')}`;
    const res = await http.get<AssessmentAutomationsDTO>(url, http.getRequestConfig(accessToken));

    return mapFromAssessmentAutomationsDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** Assessment tasks for the items of one family; an item without a set (AUDIT0039) or not allowed is refused, the others are created. */
export async function apiCreateAssessmentTasks(
  create: AssessmentTaskCreate,
  accessToken: string,
): Promise<AssessmentTaskResult> {
  try {
    const res = await http.post<AssessmentTaskResultDTO>(
      '/criteria/assess/batch',
      mapToAssessmentTaskCreateDTO(create),
      http.getRequestConfig(accessToken),
    );

    return mapFromAssessmentTaskResultDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * The AI evidence audit (PLAN-077) of an open assessment task's item, with the task as its consumer (PLAN-CTI-090): the
 * server starts it in the background and answers the audit's line, started or why not.
 */
export async function apiRunAssessmentEvidenceAudit(
  taskId: number,
  accessToken: string,
): Promise<AssessmentTaskCreated> {
  try {
    const res = await http.post<AssessmentTaskCreatedDTO>(
      `/criteria/assess/${taskId}/run`,
      undefined,
      http.getRequestConfig(accessToken),
    );

    return mapFromAssessmentTaskCreatedDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** One AI outcome, for its details (the evidence links); not found when it does not exist or its item is not readable. */
export async function apiGetCriterionOutcome(outcomeId: number, accessToken: string): Promise<AICriterionOutcome> {
  try {
    const res = await http.get<AICriterionOutcomeDTO>(
      `/criteria/outcomes/${outcomeId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromAICriterionOutcomeDTO(res.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
